import React, { useState, useMemo, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Checkbox, Form, TextField } from "../../../components/forms";
import EditAppointmentPopup from "./EditAppointmentPopup";
import { useGetMyProfileQuery, useUpdateProfileMutation } from "../../../services/userService";
import { useGetAppointmentsQuery } from "../../../services/orderService";
import parseError from "../../../utils/errorUtils";
import { toast } from "react-toastify";
import { useLoggerUtils } from "../../../utils/loggerUtils";
import {
  useAddCheckoutPaymentMutation,
  useGetPricingInfoQuery
} from '../../../services/paymentService';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import { customSelectStyles } from "../../../constants/styles";
import {
  useLazyGetAddressesQuery,
  useLazyGetPostcodesQuery,
} from "../../../services/dataService";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { DEBOUNCE_DELAY } from "../../../constants";
import { debounce } from "lodash";
import { cleanSpaces, getDisplayAddress } from "../../../utils/dataUtils";
import {
  useUpdateHubSpotMutation
} from "../../../services/dataService"

const OrderConfirmation = () => {
  const [showModal, setShowModal] = useState(false);
  const { data: profileData } = useGetMyProfileQuery();
  const { info } = useLoggerUtils();
  const [refreshKey, setRefreshKey] = useState(1);
  const [postCodeOptions, setPostCodeOptions] = useState([]);
  const [postCode, setPostCode] = useState();
  const [address, setAddress] = useState();
  const [city, setCity] = useState();
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [addressList, setAddressList] = useState([
    { value: 0, label: "Enter address manually" },
  ]);

  const [updateProfile] = useUpdateProfileMutation();
  const [getPostCodes, { isFetching: postCodeLoading }] =
    useLazyGetPostcodesQuery();
  const [getAddresses, { isFetching: addressLoading }] =
    useLazyGetAddressesQuery();
  const { data: subscriptionPricing, isSubscriptionLoading } = useGetPricingInfoQuery({
    type: "Subscription"
  });
  const { data: initialPricing, isInitialLoading } = useGetPricingInfoQuery({
    type: "Initial"
  });
  const [updateHubSpot] = useUpdateHubSpotMutation();

  const debouncedChangeHandler = useMemo(
    () => debounce((userInput) => getOptions(userInput), DEBOUNCE_DELAY),
    []
  );

  const getOptions = async (value) => {
    try {
      if (value) {
        const res = await getPostCodes({ partial: value });

        if (res.data) {
          const transformedOptions = res.data.result?.map((optn) => ({
            label: optn,
            value: optn,
          }));

          setPostCodeOptions(transformedOptions);
        } else {
          setPostCodeOptions([]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getAddressList = async (value) => {
    const postCode = value.replace(/ /g, "");

    try {
      if (value) {
        const res = await getAddresses({ postcode: postCode });

        if (res.data) {
          const addressList = res.data.addresses.map((address, i) => {
            const addr = address.split(",");
            const result = {
              line1: cleanSpaces(addr[0]),
              line2: cleanSpaces(addr[1] + " " + addr[2] + " " + addr[3]),
              city: cleanSpaces(addr[5]),
              county: cleanSpaces(addr[6]),
              postcode: value,
            };

            return {
              value: i + 1,
              label: getDisplayAddress(result),
              address: result,
            };
          });

          setAddressList([
            { value: 0, label: "Enter address manually" },
            ...addressList,
          ]);
        } else {
          setPostCodeOptions([]);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSelectAddress = (value) => {
    const selectedAddress = addressList.find(({ label }) => label === value);

    if (selectedAddress.value === 0) {
      setAddressList([{ value: 0, label: "Enter address manually" }]);
    }
    setAddress(
      selectedAddress || {
        postcode: postCode,
      }
    );
  };

  const handlePostalInputChange = (value) => {
    debouncedChangeHandler(value);
  };

  const { data: appointmentList, isFetching } = useGetAppointmentsQuery({
    page: 0,
    pageSize: 320,
    type: "full",
  });
  const [addStripeCheckout] = useAddCheckoutPaymentMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState("SUBSCRIPTION");
  const leftRef = useRef(null);
  const [height, setHeight] = useState("auto");
  const [consent, setConsent] = useState(false)
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [disable, setDisable] = useState(true);

  const handleRadioChange = (option) => {
    setSelectedOption(option);
  };

  const baseURL = window.location.href.split('onboarding/')[0];

  const selectedAppointment = appointmentList?.content?.find(item => item.id === Number(localStorage.getItem("appointment_id"))) || {};

  const toggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const savedValues = JSON.parse(
    localStorage.getItem("EligibilityForm") || "null"
  );

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .matches(/^[A-Za-z\s]*$/, "First name can only contain letters"),
    lastName: Yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .matches(/^[A-Za-z\s]*$/, "Last name can only contain letters"),
    email: Yup.string()
      .required("Email address is required")
      .email("Please enter a valid email address"),
    phone: Yup.string()
      .required("Phone number is required")
      .test("starts-with-plus", "Country code is required (e.g., +44)", (value) =>
        /^\+/.test(value || "")
      )
      .test("valid-length", "Please enter a valid phone number", (value) =>
        /^\+[0-9]{11,13}$/.test(value || "")
      ),
    identity_consent: Yup.boolean().oneOf([true], "Please check the consent form"),
    valid_ID_consent: Yup.boolean().oneOf([true], "Please check the consent form"),
    terms_and_conditions: Yup.boolean().oneOf([true], "Please accept the above consent"),
  });

  const initialValues = {
    firstName: savedValues?.["First name"] || "",
    lastName: savedValues?.["Last name"] || "",
    email: savedValues?.["Email address"] || "",
    phone: savedValues?.["Phone number"] || "+44",
    identity_consent: false,
    valid_ID_consent: false,
    terms_and_conditions: false,
  };

  useEffect(() => {
    if (leftRef.current) {
      setHeight(leftRef.current.clientHeight + "px");
    }
    setTimeout(() => {
      setRefreshKey((prev) => prev + 1);
    }, 2000);
  }, []);

  const handleSubmit = async (values) => {
    setFirstSubmit(true);
    const objectData = {
      ...savedValues,
      "Email address": values.email,
      "First name": values.firstName,
      "Last name": values.lastName,
      "Phone number": values.phone,
    };
    const data = JSON.stringify({ ...objectData });
    localStorage.setItem("EligibilityForm", data);

    try {
      const data = {
        ...profileData,
        ...values,
        gender: savedValues?.["Gender at birth"],
        address: address?.address
          ? address.address
          : {
            city: city,
            county: "",
            line1: address,
            line2: "",
            postcode: postCode.label,
          },
        dateOfBirth: savedValues?.["Date of birth"],
        regStatus: "PERSONAL_INFORMATION",
      };
      delete data.identity_consent;
      delete data.valid_ID_consent;
      delete data.terms_and_conditions;

      const postcode = postCode.label;

      if (!postcode) {
        toast.error(
          "You must provide a valid post code to proceed with registration to Treat It; otherwise, you cannot continue"
        );
      } else if (postcode.slice(0, 2).toLowerCase().includes("bt")) {
        toast.error("Location not currently supported");
      } else if (localStorage.getItem("appointment_id") === "null") {
        toast.error("Select an appointment");
      } else {
        setIsSubmitting(true);
        const res = await updateProfile({ values: data });

        if (res.error) {
          setIsSubmitting(false);
          toast.error(parseError(res.error, "Couldn't update profile"));
        } else {
          await checkOut();
        }
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error(error);
    }
  };

  const checkOut = async () => {
    // localStorage.setItem("orderId", orderId);
    localStorage.setItem("paymentMode", selectedOption)
    try {
      const data = {
        quantity: 1,
        customerId: profileData?.id,
        successUrl: `${baseURL}onboarding/eligibility-flow/order-success`,
        cancelUrl: `${baseURL}onboarding/eligibility-flow/order-confirm`,
        orderItemId: profileData?.id, //order item will be replaced from the back end with checkout confirm API call 
        description: "initial order",
        paymentMode: selectedOption === "SUBSCRIPTION" ? "SUBSCRIPTION" : "PAYMENT"
      }

      const res = await addStripeCheckout({ values: data });

      if (res.error) {
        setIsSubmitting(false);
        toast.error(parseError(res.error, "Stripe checkout failed"));
      } else {
        setIsSubmitting(false);
        localStorage.setItem("sessionId", res.data.sessionId)
        await submitHubSpot();
        res.data && (window.location.href = res.data.url)
      }

    } catch (e) {
      setIsSubmitting(false);
      info("addOrder - catch: " + JSON.stringify(e));
      console.error(e);
    }
  }

  const dateSlots = useMemo(() => {
    let arr = [];
    appointmentList?.content.forEach(({ datetime }) => {
      const formattedDate = datetime.split("T")[0]

      arr.push(formattedDate);
    });

    let unique = [...new Set(arr)];

    return unique;
  }, [appointmentList?.content]);

  const handleCheckTermsChange = (e) => {
    const isChecked = e.target.checked;
    setIsTermsChecked(isChecked);
  };

  const handleCheckConsent = (e) => {
    const isChecked = e.target.checked;
    setConsent(isChecked);
  }

  useEffect(() => {
    setDisable(!address || !city || !isTermsChecked || !consent)
  }, [address, city, isTermsChecked, consent])

  const submitHubSpot = async () => {
    const vid = localStorage.getItem("vid")
    try {
      let data = {
        properties: [
          {
            property: "slot_id",
            value: localStorage.getItem("appointment_date") + " " + localStorage.getItem("appointment_time")
          },
          {
            property: "price_plan",
            value: selectedOption === "SUBSCRIPTION" ?
              "Monthly Clinic Subscription" :
              "Pay As You Go"
          },
          {
            property: "final_status",
            value: "Completed"
          },
        ]
      };
      const updateRes = vid && await updateHubSpot({ values: data, vid: vid })

      if (updateRes.data.status === "Success") {
      } else {
        console.error("hubspot faild")
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="d-flex flex-column h-100">
      <h4 className="mb-2 fw-bold">Billing details</h4>
      <p className="text-gray-1 mb-2">Please provide your card details to book your appointment.</p>
      <div className="container px-0 d-flex flex-column justify-content-between mt-4">
        <div className="column col-md-12 col-lg-12 col-xl-9">
          <div className="row mb-3 mb-md-4">
            <div className="col-md-12 mb-3 mb-lg-0">
              <div className="d-flex flex-row align-items-center justify-content-between px-4 py-2 gap-3 gap-lg-6 appointment-bg border-0 rounded">
                <div className="d-flex flex-column align-items-start align-items-lg-center gap-3 py-2">
                  <div className="d-flex flex-row align-items-center gap-3 py-1 py-lg-2">
                    <img
                      src="/images/eligibility/note.svg"
                      alt="appointment date and time"
                      className="pathway-icon"
                    />
                    {localStorage.getItem("appointment_date") !== "-" && localStorage.getItem("appointment_time") !== "-" ? (
                      <div>
                        <p className="text-primary-600 mb-1">Appointment date & time</p>

                        <p className="fs-6 fw-bold flow-gap mb-0" key={refreshKey}>
                          {localStorage.getItem("appointment_date")} | {localStorage.getItem("appointment_time")}
                        </p></div>
                    ) : (
                      <p className="text-primary-600 flow-gap mb-0">Please book an appointment with the date and time.</p>
                    )}
                  </div>
                  <button
                    className="d-block d-lg-none bg-white text-primary-600 py-1 px-3 ms-8 fw-bold fs-6 w-fit border-0 rounded-1 text-nowrap"
                    onClick={toggleModal}
                  >
                    {localStorage.getItem("appointment_date") !== "-" && localStorage.getItem("appointment_time") !== "-" ? (
                      <p className="mb-0">Change date & time</p>) : (
                      <p className="mb-0">Select date & time</p>)}
                  </button>
                </div>

                <button
                  className="d-none d-lg-block bg-white text-primary-600 py-1 px-3 fw-bold fs-6 border-0 rounded-1 text-nowrap"
                  onClick={toggleModal}
                >
                  {localStorage.getItem("appointment_date") !== "-" && localStorage.getItem("appointment_time") !== "-" ? (
                    <p className="mb-0">Change date & time</p>) : (
                    <p className="mb-0">Select date & time</p>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex h-100">
        <Form
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={city && address && selectedOption && handleSubmit}
          enableReinitialize
          className="d-flex"
        >
          <div className="container px-0 d-flex flex-column justify-content-between">
            <div className="column col-md-12 col-lg-12 col-xl-9">
              <div className="container px-0 d-flex flex-column justify-content-between">
                <div className="column col-md-12 col-lg-12 col-xl-9 w-100">
                  <label className="mb-2">
                    Select pricing plan<span className="text-red-1">*</span>
                  </label>
                </div>
                <div className="row gap-2 gap-lg-0">
                  <div className="column col-12 col-lg-6 col-xl-6 pt-0 pb-2 pe-lg-2 cursor-pointer" onClick={() => handleRadioChange("SUBSCRIPTION")}>
                    <div className={`px-0 d-flex flex-column justify-content-between pt-3 pb-3 rounded-1 w-100 h-100 ${selectedOption === "SUBSCRIPTION" ? "hr-radio-selected" : "hr-radio"}`}>
                      <div ref={leftRef} className="d-flex flex-column px-4 align-items-start justify-content-between">
                        <div className="form-check form-check-inline">
                          <input className="form-check-input" type="radio" name="monthly" value="monthly"
                            checked={selectedOption === "SUBSCRIPTION"}
                            onChange={() => handleRadioChange("SUBSCRIPTION")} />
                          <label className="form-check-label text-primary fs-6 lh-sm">Monthly Clinic Subscription</label>
                        </div>
                        <div className="d-flex flex-row gap-2 align-items-center justify-content-between mt-2">
                          <p className="mb-0 text-primary fw-bold fs-5">
                            £{subscriptionPricing?.amount || "14.5"}
                            <span class="small fw-normal opacity-75 size-14"> / month</span>
                          </p>
                        </div>
                        <div className="d-flex flex-row gap-2 align-items-center justify-content-between">
                          <p className="mb-0 text-primary fw-bold size-14">
                            *£{"11.5"}<span class="size-14 fw-normal opacity-75"> / month from the 2nd year onwards</span>
                          </p>
                        </div>
                      </div>
                      <hr className="my-4 hr-radio" />
                      <p className="mb-3 px-4 text-primary fw-bold">
                        This subscription includes all of the following services:
                      </p>
                      <div className="d-flex flex-column mb-2 px-4 align-items-start justify-content-between">
                        <ul className="d-flex flex-column gap-2 list-unstyled mb-0">
                          <li>
                            <span>✔<span className="text-primary fw-bold ms-2">FREE </span></span>
                            Initial consultation
                          </li>
                          <li>
                            <span>✔<span className="text-primary fw-bold ms-2">FREE </span></span>
                            Monthly repeat prescription
                          </li>
                          <li className="mb-1">
                            <span>✔<span className="text-primary fw-bold ms-2">FREE </span></span>
                            6 month follow-up consultation
                          </li>
                        </ul>
                      </div>
                      <div className="flex-row gap-2 mx-4 align-items-center justify-content-between mt-auto">
                        <p className="mb-0 text-primary text-center px-2 radio-btn-tag rounded-3 fw-bold w-100">
                          • <span class="small fw-normal ms-1">Minimum 12-month commitment</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="column col-12 col-lg-6 col-xl-6 pt-0 pb-2 ps-lg-2 cursor-pointer" onClick={() => handleRadioChange("PAYMENT")}>
                    <div className={`px-0 d-flex flex-column justify-content-between pt-3 pb-3 rounded-1 w-100 ${selectedOption === "PAYMENT" ? "hr-radio-selected" : "hr-radio"}`}>
                      <div className="d-flex flex-column px-4 align-items-start" style={{ height }}>
                        <div className="form-check form-check-inline">
                          <input className="form-check-input" type="radio" name="perConsultation" value="perConsultation"
                            checked={selectedOption === "PAYMENT"}
                            onChange={() => handleRadioChange("PAYMENT")}
                          />
                          <label className="form-check-label text-primary fs-6 lh-sm">Pay As You Go</label>
                        </div>
                        <div className="d-flex flex-row gap-2 align-items-center justify-content-between mt-2">
                          <p className="mb-0 text-primary fw-bold fs-5 line lh-1">
                            £{initialPricing?.amount || "69"}
                            <span class="small fw-normal opacity-75 size-14"> - Pay for your initial consultation now</span>
                          </p>
                        </div>
                      </div>
                      <hr className="my-4 hr-radio" />
                      <p className="mb-3 px-4 text-primary fw-bold">
                        Pay As You Go for each of the following services:
                      </p>
                      <div className="d-flex flex-column mb-2 px-4 align-items-start justify-content-between">
                        <ul className="d-flex flex-column gap-2 list-unstyled mb-0">
                          <li>
                            <span>✔<span className="text-primary fw-bold ms-2">
                              £{initialPricing?.amount || "69"} - </span>
                            </span>
                            Initial consultation
                          </li>
                          <li className="d-flex gap-2">
                            <span>✔</span>
                            <div>
                              <span className="text-primary fw-bold">£{"9.99"} <span class="size-14 fw-normal">(Repeat each month)</span> - </span>
                              Repeat prescription
                            </div>
                          </li>
                          <li className="mb-1 d-flex gap-2">
                            <span>✔</span>
                            <div>
                              <span className="text-primary fw-bold">£{"49.99"} - </span>
                              6 month follow-up consultation
                            </div>
                          </li>
                        </ul>
                      </div>
                      <div className="flex-row gap-2 mx-4 align-items-center justify-content-between mt-1">
                        <p className="mb-0 text-primary text-center px-2 radio-btn-tag rounded-3 fw-bold w-100">
                          • <span className="small fw-normal ms-1">No monthly commitment</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {selectedOption === null && firstSubmit && (
                  <label className="text-danger mt-1">
                    Pricing plan is required
                  </label>
                )}
              </div>
              <p className="mt-4 fw-bold">Personal details</p>
              <div className="row mb-4 mt-4">
                <div className="col-md-12 col-lg-6 mb-3 mb-lg-0">
                  <label className="mb-2">
                    First name<span className="text-red-1">*</span> (as
                    displayed on an ID)
                  </label>
                  <TextField
                    name="firstName"
                    placeholder="Enter your first name"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                  />
                </div>
                <div className="col-md-12 col-lg-6">
                  <label className="mb-2">
                    Last name<span className="text-red-1">*</span> (as
                    displayed on an ID)
                  </label>
                  <TextField
                    name="lastName"
                    placeholder="Enter your last name"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                  />
                </div>
              </div>
              <div className="row mb-4 mt-4">
                <div className="col-md-12 col-lg-6 mb-3 mb-lg-0">
                  <label className="mb-2">
                    Email address<span className="text-red-1">*</span>
                  </label>
                  <TextField
                    name="email"
                    type="email"
                    placeholder="Enter your email address"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder "
                  />
                </div>
                <div className="col-md-12 col-lg-6">
                  <label className="mb-2">
                    Phone number<span className="text-red-1">*</span>
                  </label>
                  <TextField
                    name="phone"
                    placeholder="Enter your phone number"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 bg-transparent border card-border rounded custom-placeholder"
                    onKeyDown={(e) => {
                      const inputValue = e.currentTarget.value;
                      const allowedKeys = [
                        "Backspace",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Delete",
                      ];
                      if (
                        (!/[0-9]/.test(e.key) && e.key !== "+" && !allowedKeys.includes(e.key)) ||
                        // (e.key === "+" && e.target.selectionStart !== 0)
                        (inputValue.length <= 3 && e.key === "Backspace")
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-md-12 col-lg-6 mb-3 mb-lg-0">
                  <label className="mb-2">
                    Post code<span className="text-red-1">*</span>
                  </label>
                  <Select
                    name="postCode"
                    placeholder="Select"
                    styles={customSelectStyles}
                    errorClass="error-text mt-1"
                    options={postCodeOptions}
                    isSearchable
                    onInputChange={handlePostalInputChange}
                    isLoading={postCodeLoading}
                    onChange={(option) => {
                      getAddressList(option.value);
                      setPostCode(option);
                    }}
                  />
                  {postCode === undefined && firstSubmit && (
                    <label className="text-danger mt-1">
                      Postcode is required
                    </label>
                  )}
                </div>
                <div className="col-md-12 col-lg-6">
                  <label className="mb-2">
                    Address<span className="text-red-1">*</span>
                  </label>
                  <CreatableSelect
                    name="address"
                    placeholder="Enter your address"
                    styles={customSelectStyles}
                    errorClass="error-text mt-1"
                    options={addressList}
                    isSearchable
                    isLoading={addressLoading}
                    onChange={(option) => {
                      if (option.address) {
                        handleSelectAddress(option?.label);
                        setCity(option?.address?.city || "");
                      } else {
                        setAddress(option.label);
                        setCity("");
                      }
                    }}
                  />
                  {address === undefined && firstSubmit && (
                    <label className="text-danger mt-1">
                      Address is required
                    </label>
                  )}
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-md-12 col-lg-6 mb-3 mb-lg-0">
                  <label className="mb-2">
                    City<span className="text-red-1">*</span>
                  </label>
                  <input
                    name="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Autofill based on post code"
                    autoComplete="no"
                    className="p-2 px-3 fs-6 w-100 bg-transparent border card-border rounded custom-placeholder"
                  />
                  {(city === undefined || city === "") && firstSubmit && (
                    <label className="text-danger mt-1">
                      City is required
                    </label>
                  )}
                </div>
              </div>
              <div className="row mb-4 mt-5">
                <div className="col-md-12 mb-4">
                  <Checkbox
                    label="I am making this appointment for myself and not on behalf of another person, including children under the age of 18."
                    name="identity_consent"
                    className="check-lg"
                    inputClass="rounded"
                    labelClass="ms-3"
                    onChange={handleCheckConsent}
                  />
                </div>
                <div className="col-md-12 mb-4">
                  <Checkbox
                    label="I confirm that I have a valid form of government-issued photo ID (e.g., UK NIC, driving licence, passport) ready to present on camera for identity verification before my video consultation."
                    name="valid_ID_consent"
                    className="check-lg"
                    inputClass="rounded"
                    labelClass="ms-3"
                    onChange={handleCheckConsent}
                  />
                </div>
                <div className="col-md-12 mb-12 mb-md-0">
                  <Checkbox
                    label={
                      <>
                        I have read and understood the{" "}
                        <a
                          href="https://treat-it.clinic/terms-conditions/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 fw-semibold text-decoration-underline"
                        >
                          Terms and Conditions
                        </a>{" "}
                        prior to booking a consultation.
                      </>
                    }
                    name="terms_and_conditions"
                    className="check-lg"
                    inputClass="rounded"
                    labelClass="ms-3"
                    onChange={handleCheckTermsChange}
                  />
                </div>
              </div>
            </div>
            <div className="column d-flex flex-row justify-content-center justify-content-md-end align-items-center">
              <div className="d-flex flex-column-reverse flex-md-row footer-div px-5 px-md-0 py-4 py-md-0">
                <button
                  className={`bg-primary-600 ${(disable || isSubmitting) ? "opacity-50" : "opacity-100"}  text-white py-2 px-5 w-100 w-md-fit h-fit fw-bold fs-6 border-0 rounded text-nowrap`}
                  type="submit"
                  onClick={() => setFirstSubmit(true)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <LoadingSpinner loadingText="Continue" /> : "Continue"}
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>

      <EditAppointmentPopup
        show={showModal}
        onClose={toggleModal}
        dateSlots={dateSlots}
        selectedDay={selectedAppointment?.datetime}
        availableAppointments={appointmentList?.content || []}
      />
    </div>
  );
};

export default OrderConfirmation;
